import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Plus, Minus } from 'lucide-react';

const StepWallingFencing = ({
  wallingFencingRequired,
  setWallingFencingRequired,
  wallingFencingItems,
  onWallingFencingChange,
  totals,
  onBack,
  onContinue
}) => {
  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8">
      <h2 className="text-xl md:text-2xl font-bold text-[#27292E] mb-2">
        Walling & Fencing
      </h2>
      <p className="text-gray-600 mb-6">
        Is there any walling or fencing on this project?
      </p>
      
      {/* Yes/No Toggle - Always Visible */}
      <div className="flex gap-4 mb-6">
        <button
          onClick={() => setWallingFencingRequired(true)}
          className={`flex-1 py-4 px-6 rounded-xl font-medium text-lg transition-colors ${
            wallingFencingRequired === true 
              ? 'bg-[#300122] text-white' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          data-testid="walling-fencing-yes"
        >
          Yes
        </button>
        <button
          onClick={() => setWallingFencingRequired(false)}
          className={`flex-1 py-4 px-6 rounded-xl font-medium text-lg transition-colors ${
            wallingFencingRequired === false 
              ? 'bg-[#300122] text-white' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          data-testid="walling-fencing-no"
        >
          No
        </button>
      </div>

      {/* No walling/fencing needed */}
      {wallingFencingRequired === false && (
        <p className="text-gray-500 text-center py-4">
          No walling or fencing required for this project.
        </p>
      )}

      {/* Walling/Fencing Items */}
      {wallingFencingRequired === true && (
        <>
          <p className="text-gray-600 mb-4">
            Add the walling and fencing items for this project.
          </p>

          <div className="space-y-6">
            {wallingFencingItems.map((item) => (
              <div 
                key={item.id}
                className={`border-2 rounded-xl p-5 transition-colors ${
                  (item.unit === 'm²' ? ((item.height || 0) > 0 && (item.length || 0) > 0) : (item.quantity || 0) > 0)
                    ? 'border-[#300122] bg-[#300122]/5' 
                    : 'border-gray-200'
                }`}
                data-testid={`walling-${item.id}`}
              >
                <div className="flex flex-col gap-4">
                  {/* Header with name and price adjustment */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1">
                      <h3 className="font-bold text-[#27292E]">{item.name}</h3>
                      <p className="text-sm text-gray-500">{item.description}</p>
                    </div>
                    
                    {/* Price adjustment */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Price:</span>
                      <button
                        onClick={() => onWallingFencingChange(item.id, 'pricePerUnit', item.pricePerUnit - item.priceIncrement)}
                        className="w-8 h-8 flex items-center justify-center bg-white border-2 border-[#300122] text-[#300122] rounded-lg hover:bg-[#300122]/10"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="font-bold text-[#300122] text-lg min-w-[80px] text-center">
                        £{item.pricePerUnit}
                      </span>
                      <button
                        onClick={() => onWallingFencingChange(item.id, 'pricePerUnit', item.pricePerUnit + item.priceIncrement)}
                        className="w-8 h-8 flex items-center justify-center bg-[#300122] text-white rounded-lg hover:bg-[#4a0636]"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      {item.pricePerUnit !== item.basePrice && (
                        <span className="text-xs text-gray-400">(base: £{item.basePrice})</span>
                      )}
                    </div>
                  </div>

                  {/* Quantity/Dimensions inputs based on type */}
                  <div className="flex flex-wrap items-center gap-4">
                    {item.unit === 'm²' ? (
                      // Wall items need height and length
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">Height:</span>
                          <Input
                            type="number"
                            min="0"
                            step="0.1"
                            value={item.height || ''}
                            onChange={(e) => onWallingFencingChange(item.id, 'height', e.target.value)}
                            placeholder="0"
                            className="w-20 h-10 text-center border-2 rounded-lg"
                          />
                          <span className="text-sm text-gray-500">m</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">Length:</span>
                          <Input
                            type="number"
                            min="0"
                            step="0.1"
                            value={item.length || ''}
                            onChange={(e) => onWallingFencingChange(item.id, 'length', e.target.value)}
                            placeholder="0"
                            className="w-20 h-10 text-center border-2 rounded-lg"
                          />
                          <span className="text-sm text-gray-500">m</span>
                        </div>
                        {(item.height || 0) > 0 && (item.length || 0) > 0 && (
                          <span className="text-sm text-[#300122] font-medium">
                            = {((item.height || 0) * (item.length || 0)).toFixed(2)} m²
                          </span>
                        )}
                      </>
                    ) : item.id === 'brick_pillar' ? (
                      // Pillar needs width, height and quantity
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">Width (mm):</span>
                          <Input
                            type="text"
                            value={item.width || ''}
                            onChange={(e) => onWallingFencingChange(item.id, 'width', e.target.value)}
                            placeholder="e.g. 450"
                            className="w-24 h-10 text-center border-2 rounded-lg text-sm"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">Height (mm):</span>
                          <Input
                            type="text"
                            value={item.height || ''}
                            onChange={(e) => onWallingFencingChange(item.id, 'height', e.target.value)}
                            placeholder="e.g. 1200"
                            className="w-24 h-10 text-center border-2 rounded-lg text-sm"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => onWallingFencingChange(item.id, 'quantity', Math.max(0, (item.quantity || 0) - 1))}
                            className="w-10 h-10 flex items-center justify-center bg-white border-2 border-[#300122] text-[#300122] rounded-lg hover:bg-[#300122]/10"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <Input
                            type="number"
                            min="0"
                            value={item.quantity || ''}
                            onChange={(e) => onWallingFencingChange(item.id, 'quantity', e.target.value)}
                            placeholder="0"
                            className="w-16 h-10 text-center border-2 rounded-lg"
                          />
                          <button
                            onClick={() => onWallingFencingChange(item.id, 'quantity', (item.quantity || 0) + 1)}
                            className="w-10 h-10 flex items-center justify-center bg-[#300122] text-white rounded-lg hover:bg-[#4a0636]"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                          <span className="text-sm text-gray-500">pillars</span>
                        </div>
                      </>
                    ) : (
                      // Standard quantity input
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onWallingFencingChange(item.id, 'quantity', Math.max(0, (item.quantity || 0) - 1))}
                          className="w-10 h-10 flex items-center justify-center bg-white border-2 border-[#300122] text-[#300122] rounded-lg hover:bg-[#300122]/10"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <Input
                          type="number"
                          min="0"
                          value={item.quantity || ''}
                          onChange={(e) => onWallingFencingChange(item.id, 'quantity', e.target.value)}
                          placeholder="0"
                          className="w-20 h-10 text-center border-2 rounded-lg"
                        />
                        <button
                          onClick={() => onWallingFencingChange(item.id, 'quantity', (item.quantity || 0) + 1)}
                          className="w-10 h-10 flex items-center justify-center bg-[#300122] text-white rounded-lg hover:bg-[#4a0636]"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                        <span className="text-sm text-gray-500">
                          {item.unit === 'section' ? 'sections' : item.unit === 'm' ? 'metres' : item.unit}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Note field */}
                  <div>
                    <Label className="text-sm text-gray-600 mb-1 block">
                      {item.id.includes('fencing') ? 'Fence Panel Type:' : 
                       item.id.includes('wall') ? 'Brick Type:' : 
                       'Notes:'}
                    </Label>
                    <Input
                      type="text"
                      value={item.note || ''}
                      onChange={(e) => onWallingFencingChange(item.id, 'note', e.target.value)}
                      placeholder={item.id.includes('fencing') ? 'e.g. Close board, Lap panel...' : 
                                  item.id.includes('wall') ? 'e.g. Red engineering brick...' : 
                                  'Add any notes...'}
                      className="h-10 border-2 rounded-lg text-sm"
                    />
                  </div>

                  {/* Subtotal */}
                  {((item.unit === 'm²' && (item.height || 0) > 0 && (item.length || 0) > 0) || 
                    (item.unit !== 'm²' && (item.quantity || 0) > 0)) && (
                    <div className="pt-4 border-t border-gray-200 flex justify-between items-center">
                      <span className="text-gray-600 text-sm">
                        {item.unit === 'm²' 
                          ? `${((item.height || 0) * (item.length || 0)).toFixed(2)} m² × £${item.pricePerUnit}/m²`
                          : `${item.quantity} ${item.unit === 'section' ? 'section(s)' : item.unit === 'm' ? 'm' : item.unit} × £${item.pricePerUnit}`
                        }
                      </span>
                      <span className="font-bold text-[#300122] text-lg">
                        £{(item.unit === 'm²' 
                          ? ((item.height || 0) * (item.length || 0)) * item.pricePerUnit 
                          : (item.quantity || 0) * item.pricePerUnit
                        ).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Running Total and Navigation - always visible once Yes/No is selected */}
      {wallingFencingRequired !== null && (
        <>
          <div className="mt-6 p-4 bg-[#300122] text-white rounded-xl">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-white/80">Surfacing:</span>
                <span>£{totals.surfacingTotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
              </div>
              {totals.additionalTotal > 0 && (
                <div className="flex justify-between">
                  <span className="text-white/80">Additional Costs:</span>
                  <span>£{totals.additionalTotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                </div>
              )}
              {totals.edgingsTotal > 0 && (
                <div className="flex justify-between">
                  <span className="text-white/80">Edgings:</span>
                  <span>£{totals.edgingsTotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                </div>
              )}
              {totals.wallingFencingTotal > 0 && (
                <div className="flex justify-between">
                  <span className="text-white/80">Walling & Fencing:</span>
                  <span>£{totals.wallingFencingTotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-white/20">
                <span className="font-bold">Total:</span>
                <span className="text-2xl font-bold">
                  £{totals.grandTotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>

          <div className="flex gap-4 mt-8">
            <Button
              onClick={onBack}
              variant="outline"
              className="flex-1 h-14 text-lg font-medium rounded-xl border-2 border-[#300122] text-[#300122] hover:bg-[#300122]/5"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <Button
              onClick={onContinue}
              className="flex-1 bg-[#300122] hover:bg-[#4a0636] text-white h-14 text-lg font-bold rounded-xl"
            >
              Continue
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default StepWallingFencing;
